'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';
import type { ButtonHTMLAttributes, ReactNode, RefObject } from 'react';
import { cx } from '../utils';
import type { UnsafeProps } from '../escape-hatches';

/* ── Public types ────────────────────────────────── */

export type FABIntent = 'primary' | 'secondary' | 'theme';
export type FABPosition = 'bottom-right' | 'bottom-left' | 'bottom-center';

interface FABBaseProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'aria-label'>, UnsafeProps {
  /** Icon element rendered inside the FAB. Required for both forms. */
  icon: ReactNode;
  /** @default 'primary' */
  intent?: FABIntent;
  /** Viewport-fixed position. @default 'bottom-right' */
  position?: FABPosition;
  /** When true, FAB hides on scroll-down and reappears on scroll-up. */
  scrollHide?: boolean;
  /** Ref to a custom scroll container. Defaults to window. */
  scrollRef?: RefObject<HTMLElement | null>;
  /** When true, shows spinner, sets aria-busy, and prevents click. */
  loading?: boolean;
  /** Render via createPortal into document.body. Required when the FAB is
   *  inside a container with `transform`, `filter`, or `perspective` — these
   *  create a new containing block that breaks `position: fixed`. */
  portal?: boolean;
  /** Adopter className — merged via cascade layer rules. */
  className?: string;
}

/** Icon-only form: ariaLabel is REQUIRED, no children. */
interface FABIconOnlyProps extends FABBaseProps {
  /** Required accessible name for icon-only FAB. */
  ariaLabel: string;
  children?: never;
}

/** Extended form: children (visible label) REQUIRED, ariaLabel optional. */
interface FABExtendedProps extends FABBaseProps {
  /** Visible label text. */
  children: ReactNode;
  /** Optional override — derived from children by default. */
  ariaLabel?: string;
}

export type FABProps = FABIconOnlyProps | FABExtendedProps;

/* ── Scroll-hide hook ────────────────────────────── */

const SCROLL_HIDE_THRESHOLD = 40; // px down before hide
const SCROLL_SHOW_THRESHOLD = 20; // px up before reveal

function useScrollHide(
  enabled: boolean,
  scrollRef: RefObject<HTMLElement | null> | undefined,
): [hidden: boolean, reveal: () => void] {
  const [hidden, setHidden] = React.useState(false);
  const accumulatedRef = React.useRef(0);
  const lastScrollYRef = React.useRef(0);
  const rafRef = React.useRef<number | null>(null);

  // Track which element the listener is attached to so the effect
  // re-runs when scrollRef.current is populated after mount.
  const [scrollElement, setScrollElement] = React.useState<HTMLElement | null>(null);

  // useLayoutEffect reads scrollRef.current AFTER DOM commit (refs are
  // populated), then stores it in state to trigger the listener effect.
  React.useLayoutEffect(() => {
    setScrollElement(scrollRef?.current ?? null);
  }, [scrollRef]);

  React.useEffect(() => {
    if (!enabled) {
      // Reset hidden state when scrollHide is toggled off so the FAB
      // doesn't stay permanently invisible after a scroll-down event.
      setHidden(false);
      return;
    }

    const target: HTMLElement | Window = scrollElement ?? window;

    const getScrollY = (): number => {
      if (scrollElement) return scrollElement.scrollTop;
      return window.scrollY;
    };

    // Reset state when effect re-runs (scrollHide toggled, or element changed)
    lastScrollYRef.current = getScrollY();
    accumulatedRef.current = 0;

    const handleScroll = () => {
      if (rafRef.current != null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const currentY = getScrollY();
        const delta = currentY - lastScrollYRef.current;
        lastScrollYRef.current = currentY;

        if (delta > 0) {
          accumulatedRef.current = Math.max(0, accumulatedRef.current) + delta;
          if (accumulatedRef.current > SCROLL_HIDE_THRESHOLD) {
            setHidden(true);
          }
        } else if (delta < 0) {
          accumulatedRef.current = Math.min(0, accumulatedRef.current) + delta;
          if (accumulatedRef.current < -SCROLL_SHOW_THRESHOLD) {
            setHidden(false);
          }
        }
      });
    };

    target.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      target.removeEventListener('scroll', handleScroll);
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [enabled, scrollElement]);

  const reveal = React.useCallback(() => setHidden(false), []);

  return [hidden, reveal];
}

/* ── Component ───────────────────────────────────── */

export const FAB = React.forwardRef<HTMLButtonElement, FABProps>(function FAB(
  {
    icon,
    children,
    ariaLabel,
    intent = 'primary',
    position = 'bottom-right',
    scrollHide,
    scrollRef,
    loading,
    portal = false,
    className,
    unsafe_className,
    unsafe_style,
    type = 'button',
    onClick,
    onFocus,
    ...rest
  },
  ref,
) {
  const isExtended = children != null && children !== '';
  const [hidden, reveal] = useScrollHide(!!scrollHide, scrollRef);

  // Portal must be gated behind useEffect to avoid SSR hydration mismatch:
  // server renders inline, client would portal to document.body — irreconcilable.
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => {
    setMounted(!!portal);
  }, [portal]);

  const handleClick = loading ? undefined : onClick;

  const handleFocus = React.useCallback(
    (e: React.FocusEvent<HTMLButtonElement>) => {
      // §6.13: Focus-while-hidden reveals FAB
      reveal();
      onFocus?.(e);
    },
    [reveal, onFocus],
  );

  const ariaProps = loading ? { 'aria-busy': true as const } : {};

  const classes = cx(
    'j-fab',
    `j-fab-${intent}`,
    `j-fab-${position}`,
    isExtended && 'j-fab-extended',
    hidden && 'j-fab-hidden',
    className,
    unsafe_className,
  );

  const content = (
    <button
      {...rest}
      {...ariaProps}
      ref={ref}
      type={type}
      className={classes}
      style={unsafe_style}
      onClick={handleClick}
      onFocus={handleFocus}
      aria-label={ariaLabel}
    >
      {loading ? (
        <span className="j-fab-spinner" aria-hidden="true">
          <svg width="1em" height="1em" viewBox="0 0 16 16" fill="none">
            <circle
              cx="8"
              cy="8"
              r="6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray="28"
              strokeDashoffset="8"
            />
          </svg>
        </span>
      ) : (
        <span className="j-fab-icon" aria-hidden="true">
          {icon}
        </span>
      )}
      {isExtended && <span className="j-fab-label">{children}</span>}
    </button>
  );

  if (portal && mounted) {
    return createPortal(content, document.body);
  }

  return content;
});
